import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';

import { AppShell } from '../../../components/app-shell/AppShell';
import { EmptyState, MotionSection, PageHero } from '../../../components/ui';
import { VENUE_STATUS_LABEL, type VenueStatus } from '../../../lib/venue-status';

/**
 * /host/venues — list of venues for the host's account.
 *
 * Empty state copy is the canonical brand line for this surface — see prompt:
 * "You haven't added a venue yet. Adding one takes about 5 minutes."
 */

type VenueListItem = {
  id: string;
  slug: string;
  name: string;
  city: string;
  country: string;
  status: VenueStatus;
  total_completed_exchanges: number;
  verified_at: number | null;
  founding_cohort_member: boolean;
  founding_cohort_position: number | null;
};

type VenuesListResponse = { ok: true; venues: VenueListItem[] };
type ApiError = { ok: false; error: { code: string; message: string } };

async function fetchMyVenues(): Promise<VenuesListResponse | ApiError> {
  const r = await fetch('/api/venues/me/venues', { credentials: 'include' });
  return (await r.json()) as VenuesListResponse | ApiError;
}

function HostVenuesIndex() {
  const q = useQuery({ queryKey: ['venues', 'me'], queryFn: fetchMyVenues });

  if (q.isPending) {
    return (
      <AppShell tone="content">
        <Loader />
      </AppShell>
    );
  }

  if (q.isError || !q.data || !q.data.ok) {
    return (
      <AppShell tone="content">
        <EmptyState
          title="Couldn't load your venues."
          body={q.data && !q.data.ok ? q.data.error.message : 'Try again in a moment.'}
          action={<Button onClick={() => q.refetch()}>Retry</Button>}
        />
      </AppShell>
    );
  }

  const venues = q.data.venues;
  const isEmpty = venues.length === 0;

  return (
    <AppShell tone="content">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Hosting"
            title="Your venues"
            lede="Manage your venues, photos, and availability."
            action={
              <Group gap="xs">
                <Button
                  component={Link}
                  to="/host/venues/new"
                  variant="gradient"
                  gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                  radius="md"
                >
                  Add a venue
                </Button>
                <Button component={Link} to="/host/availability" variant="subtle" radius="md">
                  Edit availability
                </Button>
              </Group>
            }
          />
        </MotionSection>

        {isEmpty ? (
          <EmptyState
            title="You haven't added a venue yet."
            body="Adding one takes about 5 minutes. You can save as a draft and add photos later."
            action={
              <Button
                component={Link}
                to="/host/venues/new"
                variant="gradient"
                gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
              >
                Add your first venue
              </Button>
            }
          />
        ) : (
          <Stack gap="md">
            {venues.map((v) => {
              const status = VENUE_STATUS_LABEL[v.status];
              return (
                <Card key={v.id} withBorder padding="md" radius="md">
                  <Group justify="space-between" align="flex-start" wrap="wrap">
                    <Stack gap={4}>
                      <Group gap="xs">
                        <Title order={3}>{v.name}</Title>
                        <Badge color={status.color} variant="light">
                          {status.label}
                        </Badge>
                        {v.verified_at !== null && (
                          <Badge color="tide" variant="light">
                            Verified
                          </Badge>
                        )}
                        {v.founding_cohort_member && (
                          <Badge color="sun" variant="light">
                            Founding venue {v.founding_cohort_position ?? ''}
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm" c="dimmed">
                        {v.city}, {v.country}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {v.total_completed_exchanges} completed exchange
                        {v.total_completed_exchanges === 1 ? '' : 's'}
                      </Text>
                    </Stack>
                    <Group>
                      <Link
                        to="/host/venues/$id/preview"
                        params={{ id: v.id }}
                        style={{ textDecoration: 'none' }}
                      >
                        <Button variant="subtle" component="span">
                          Preview
                        </Button>
                      </Link>
                      <Link
                        to="/host/venues/$id/edit"
                        params={{ id: v.id }}
                        style={{ textDecoration: 'none' }}
                      >
                        <Button component="span">Edit</Button>
                      </Link>
                      <Link to="/host/availability" style={{ textDecoration: 'none' }}>
                        <Button variant="subtle" component="span">
                          Availability
                        </Button>
                      </Link>
                    </Group>
                  </Group>
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>
    </AppShell>
  );
}

export const Route = createFileRoute('/host/venues/')({
  component: HostVenuesIndex,
});
